import XUILoader from '@xero/xui/react/loader';
import { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { setVisible } from '../../../helpers/header';
import { FetchError, IShopifySettingsResponse } from '../../../models';
import { deleteInstallationAction, installationSelectors } from '../../../redux/slices/InstallationSlice';
import { organisationSelectors } from '../../../redux/slices/OrganisationSlice';
import { connectionSelectors, deleteConnectionAction } from '../../../redux/slices/ConnectionSlice';
import { getSettingsAction, settingsSelectors } from '../../../redux/slices/SettingsSlice';
import { useAppDispatch } from '../../../redux/store';
import lang from '../../../static/lang/en-NZ';
import { bootstrapSelectors } from '../../../redux/slices/BootstrapSlice';
import ErrorComponent from '../../../components/ErrorComponent/ErrorComponent';
import React from 'react';
import Link from '../../../components/Link';

export interface IShopifyValidatorProps {
    setIsInvalidOrg: (isValidOrg: boolean) => void;
}

export const ShopifyValidator: FC<IShopifyValidatorProps> = ({ setIsInvalidOrg }) => {
    const dispatch = useAppDispatch();

    const installation = useSelector(installationSelectors.installation);
    const settings = useSelector(settingsSelectors.settings) as IShopifySettingsResponse;
    const organisation = useSelector(organisationSelectors.organisation);

    const shopCurrency = settings.shopDetails?.currencyCode;
    const hostName = settings.shop;

    const settingsError = useSelector(settingsSelectors.error);
    const bootstrapError = useSelector(bootstrapSelectors.error);
    const connectionError = useSelector(connectionSelectors.error);
    const installationError = useSelector(installationSelectors.error);

    const initSettingsRef = useRef(false);

    const [backendError, setBackendError] = useState<Error | undefined>();

    useEffect(() => {
        if (settingsError) {
            setBackendError(settingsError);
        }
        if (bootstrapError) {
            setBackendError(bootstrapError);
        }
        if (connectionError) {
            setBackendError(connectionError);
        }
        if (installationError) {
            setBackendError(installationError);
        }
    }, [settingsError, bootstrapError, connectionError, installationError]);

    useEffect(() => {
        const shouldInitSettings = !settings.installationId && !initSettingsRef.current;

        if (shouldInitSettings) {
            dispatch(getSettingsAction());
        }

        initSettingsRef.current = true
    }, [dispatch, settings.installationId]);

    useEffect(() => {
        if (shopCurrency) {
            if (organisation.baseCurrency?.toUpperCase() === shopCurrency.toUpperCase()) {
                if (!redirectToBilling()) {
                    setIsInvalidOrg(false);
                }
                return;
            }

            const deleteConnectionAsync = async () => {
                await dispatch(deleteConnectionAction());
                setIsInvalidOrg(true);
                window.location.href = installation.authorizeUrl!.replace('%s', hostName!);
            };

            deleteConnectionAsync();
        }
    // All dependencies used in a hook should be injected here.
    // This is to ensure that the application reacts to all state and state does not get out of sync.
    // When working in this hook please take the time to refactor, remove this comment and resolve the dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shopCurrency]);

    const redirectToBilling = (): boolean => {
        const { billing, freeTrial } = installation;

        if (billing?.hasPaidPlan && !billing?.hasFreePlan && !billing?.hasBought && !freeTrial?.isEligibleForTrial) {
            window.location.href = billing.url;
            return true;
        }

        return false;
    };

    const onGoBack = useCallback(() => {
        if (!installation.isOnboarded) {
            dispatch(deleteInstallationAction(false)).then(
                () => window.location.reload()
            );
        }
    }, [dispatch, installation.isOnboarded]);

    const goBackButton = React.useMemo(
        () => ({
            onClick: onGoBack,
            text: lang.ERROR.GO_BACK,
        }),
        [onGoBack]
    );

    useLayoutEffect(() => setVisible(false), []);

    const renderError = () => {
        return (
            <ErrorComponent
                description={
                    <div>
                        {lang.ERROR.GO_BACK_WAIT}
                        <Link displayLinkIcon={true} href={lang.ERROR.FIND_AN_ANSWER_LINK}>
                            {lang.ERROR.FIND_AN_ANSWER}
                        </Link>
                    </div>
                }
                heading={lang.ERROR.SOMETHING_WENT_WRONG}
                primaryButton={goBackButton}
            />
        );
    };

    const renderConnectingTo = () => {
        return (
            <div className='xui-heading-large xui-text-align-center xui-margin-vertical-5xlarge'>
                {`${lang.INFORMATION.CONNECTING_TO.replace('integrationName', installation.displayName)} ${
                    lang.INFORMATION.DO_NOT_REFRESH
                }`}
                <XUILoader ariaLabel='Loading' />
            </div>
        );
    };

    if (backendError) {
        const error = backendError as FetchError;
        if (error.code && error.code >= 500 && error.code < 600) {
            return renderError();
        }
    }
    return renderConnectingTo();
};
